<template>
    <div class="ldm-root">
    <transition name="ldm-fade">
        <div
            v-if="request"
            class="ldm-overlay"
            @click.self="$emit('close')"
        >
            <div class="ldm-panel">
                <div class="ldm-bar" :style="{ backgroundColor: typeColor }"></div>

                <div class="ldm-head">
                    <v-avatar size="44" color="#e0e0e0" class="mr-3">
                        <v-img v-if="pic" :src="pic" />
                        <span v-else class="ldm-initials">{{ inits }}</span>
                    </v-avatar>
                    <div class="ldm-who">
                        <div class="ldm-name">{{ personName }}</div>
                        <div class="ldm-email">{{ (request.user && request.user.email) || '' }}</div>
                    </div>
                    <v-btn icon small class="ldm-close" @click="$emit('close')">
                        <v-icon small>mdi-close</v-icon>
                    </v-btn>
                </div>

                <v-divider />

                <div class="ldm-body">
                    <div class="ldm-row">
                        <span class="ldm-label">Type</span>
                        <span class="ldm-type" :style="{ backgroundColor: typeColor }">{{ typeName }}</span>
                    </div>
                    <div class="ldm-row">
                        <span class="ldm-label">Dates</span>
                        <span>{{ dateRange }}</span>
                    </div>
                    <div class="ldm-row">
                        <span class="ldm-label">Duration</span>
                        <span>{{ duration }}</span>
                    </div>
                    <div class="ldm-row">
                        <span class="ldm-label">Status</span>
                        <span>{{ statusName }}</span>
                    </div>
                    <div class="ldm-row">
                        <span class="ldm-label">Approver</span>
                        <span>{{ approverName }}</span>
                    </div>
                    <div v-if="request.note" class="ldm-note">
                        <span class="ldm-label">Note</span>
                        <p>{{ request.note }}</p>
                    </div>
                </div>
            </div>
        </div>
    </transition>
    </div>
</template>

<script>
import { colorForType, fullName, initials, profilePicUrl } from './leaveUtils';

export default {
    name: 'LeaveDetailModal',
    props: {
        // The leave request to show; null / falsy hides the modal.
        request: { default: null },
        leaveTypes: { type: Array, default: () => [] },
        // Optional user list for nicer name / avatar resolution.
        users: { type: Array, default: () => [] },
    },
    computed: {
        personUser() {
            if (!this.request) return null;
            const uid = this.request.user_id || (this.request.user && this.request.user.id);
            return (this.users || []).find((u) => u.id === uid) || null;
        },
        personName() {
            if (this.personUser) return fullName(this.personUser);
            return (this.request && this.request.user && this.request.user.name) || 'Unknown';
        },
        pic() {
            if (this.personUser) return profilePicUrl(this.personUser);
            return this.request && this.request.user ? profilePicUrl(this.request.user) : null;
        },
        inits() {
            return initials(this.personName);
        },
        typeId() {
            if (!this.request) return null;
            return this.request.leave_type_id || (this.request.leave_type && this.request.leave_type.id);
        },
        typeColor() {
            return colorForType(this.typeId, this.leaveTypes);
        },
        typeName() {
            return (this.request && this.request.leave_type && this.request.leave_type.name) || 'Leave';
        },
        statusName() {
            return (this.request && this.request.status && this.request.status.name) || 'Approved';
        },
        approverName() {
            const a = this.request && this.request.approver;
            return (a && (a.first_name ? `${a.first_name} ${a.last_name || ''}`.trim() : a.name)) || '—';
        },
        dateRange() {
            if (!this.request) return '';
            const f = this.$date((this.request.from || '').substring(0, 10)).format('MMM D, YYYY');
            const t = this.$date((this.request.to || '').substring(0, 10)).format('MMM D, YYYY');
            if (this.request.is_half_day) {
                return `${f} (${this.request.half_day_period === 'PM' ? 'Afternoon' : 'Morning'})`;
            }
            return f === t ? f : `${f} – ${t}`;
        },
        duration() {
            if (!this.request) return '';
            if (this.request.is_half_day) return '0.5 day';
            const n = this.request.count || 0;
            return `${n} day${n === 1 ? '' : 's'}`;
        },
    },
    mounted() {
        // Attach to .v-application (not <body>, which loses the app font) so no
        // transformed ancestor breaks the fixed positioning, and so it sits
        // above the app's sticky nav.
        const host = document.querySelector('.v-application') || document.body;
        host.appendChild(this.$el);
    },
    beforeDestroy() {
        if (this.$el && this.$el.parentNode) this.$el.parentNode.removeChild(this.$el);
    },
};
</script>

<style scoped>
.ldm-overlay {
    position: fixed;
    inset: 0;
    z-index: 300; /* top app layer, above the sticky nav and Vuetify's dialog/overlay stacking */
    background: rgba(0, 0, 0, 0.45);
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 24px;
    font-family: "Roboto", "Helvetica Neue", Arial, sans-serif;
}

.ldm-overlay ::v-deep * {
    font-family: inherit;
}

.ldm-panel {
    width: 100%;
    max-width: 420px;
    max-height: calc(100vh - 48px);
    background: #fff;
    border-radius: 10px;
    box-shadow: 0 16px 56px rgba(0, 0, 0, 0.32);
    display: flex;
    flex-direction: column;
    overflow: hidden;
}

.ldm-bar {
    flex: none;
    height: 5px;
    width: 100%;
}

.ldm-head {
    flex: none;
    display: flex;
    align-items: center;
    padding: 16px 16px 14px;
}

.ldm-panel ::v-deep > hr.v-divider {
    flex: none;
}

.ldm-fade-enter-active,
.ldm-fade-leave-active {
    transition: opacity 0.18s ease;
}

.ldm-fade-enter,
.ldm-fade-leave-to {
    opacity: 0;
}

.ldm-initials {
    color: #616161;
    font-size: 13px;
    font-weight: 700;
}

.ldm-who {
    min-width: 0;
    flex: 1;
}

.ldm-name {
    font-size: 15px;
    font-weight: 700;
    color: #263238;
    line-height: 1.25;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.ldm-email {
    font-size: 12px;
    color: #90a4ae;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.ldm-close {
    flex: none;
    margin-left: 8px;
}

.ldm-body {
    flex: 1 1 auto;
    overflow-y: auto;
    padding: 12px 16px 18px;
}

.ldm-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    padding: 7px 0;
    font-size: 13px;
    color: #37474f;
    border-bottom: 1px solid #f2f2f2;
}

.ldm-row:last-of-type {
    border-bottom: none;
}

.ldm-label {
    color: #90a4ae;
    font-weight: 600;
    flex: none;
}

.ldm-type {
    display: inline-block;
    padding: 4px 12px;
    border-radius: 999px;
    color: #fff;
    font-size: 12.5px;
    font-weight: 700;
    line-height: 1.3;
    white-space: nowrap;
}

.ldm-note {
    padding-top: 10px;
}

.ldm-note .ldm-label {
    display: block;
    margin-bottom: 4px;
}

.ldm-note p {
    margin: 0;
    font-size: 13px;
    color: #37474f;
    line-height: 1.4;
    white-space: pre-wrap;
    word-break: break-word;
}
</style>
